import { useEffect, useRef, useState } from "react";
import { CONFIG } from "../../constants/config";
import { TLocation } from "../../types/Locations";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Control, Controller } from "react-hook-form";

interface Props {
  setValue: (key: keyof TLocation, value: any) => void;
  control: Control<TLocation, unknown>
}

const GoogleAddressInput = ({ onChange, onPlaceSelected, value }: {
  onChange: (value: string) => void;
  onPlaceSelected: (value: google.maps.places.PlaceResult) => void;
  value: string;
}) => {
  const [placeAutocomplete, setPlaceAutocomplete] = useState<google.maps.places.Autocomplete | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const places = useMapsLibrary("places")

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ['geometry', 'formatted_address', 'address_components']
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    const listener = placeAutocomplete.addListener('place_changed', () => {
      const place = placeAutocomplete.getPlace();
      if (place.formatted_address) {
        // Update the input value with the full formatted address
        if (inputRef.current) {
          inputRef.current.value = place.formatted_address;
        }
        onChange(place.formatted_address);
      }
      onPlaceSelected(place);
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [placeAutocomplete, onPlaceSelected, onChange]);

  return (
    <div className="autocomplete-container">
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={(e) => {
          // Prevent form submission on Enter key in the address field
          // This allows the autocomplete to work properly
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        ref={inputRef}
        placeholder="Start typing an address..."
      />
    </div>
  );
};

export const GoogleAddressInputWrapper: React.FC<Props> = ({
  control,
  setValue,
}) => {
  return (
    <APIProvider apiKey={CONFIG.GOOGLE_API_KEY}>
      <Controller
        name="address"
        control={control}
        render={({ field }) => (
          <GoogleAddressInput
            value={field.value}
            onChange={field.onChange}
            onPlaceSelected={(place: google.maps.places.PlaceResult) => {
              // Set the full formatted address
              if (place.formatted_address) {
                field.onChange(place.formatted_address);
                setValue("address", place.formatted_address);
              }

              // Set coordinates
              if (place.geometry?.location) {
                setValue("lat", place.geometry.location.lat());
                setValue("lon", place.geometry.location.lng());
              }

              // Extract address components if needed
              if (place.address_components) {
                let city = "";
                let state = "";
                let zip = "";
                let country = "";

                place.address_components.forEach(component => {
                  const types = component.types;
                  if (types.includes("locality")) {
                    city = component.long_name;
                  }
                  if (types.includes("administrative_area_level_1")) {
                    state = component.short_name;
                  }
                  if (types.includes("postal_code")) {
                    zip = component.long_name;
                  }
                  if (types.includes("country")) {
                    country = component.long_name;
                  }
                });

                // Set these values if your TLocation type has these fields
                if (city) setValue("city" as keyof TLocation, city as any);
                if (state) setValue("state" as keyof TLocation, state as any);
                if (zip) setValue("zip" as keyof TLocation, zip as any);
                if (country) setValue("country" as keyof TLocation, country as any);
              }
            }}
          />
        )}
      />
    </APIProvider>
  )
}
